/**
 * Small per-device UI preferences in localStorage (collapsed sidebar, weekend
 * toggle, ...). Storage may be unavailable, so reads fall back to the default
 * and writes are best-effort.
 */
import { useCallback, useEffect, useState } from 'react';

export function readPref<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function writePref(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Not persisted; the value still holds for this page view.
  }
}

/** useState that is remembered per device (`key` must be constant for a component instance). */
export function usePref<T>(key: string, fallback: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => readPref(key, fallback));
  const set = useCallback(
    (next: T) => {
      setValue(next);
      writePref(key, next);
    },
    [key],
  );
  return [value, set];
}

/** Sets document.title to "<title> · TransectLibre". */
export function useDocumentTitle(title: string | null | undefined): void {
  useEffect(() => {
    document.title = title ? `${title} · TransectLibre` : 'TransectLibre';
  }, [title]);
}
